/**
 * Reading React's component stack off a crash.
 *
 * This lives in src/lib as plain TypeScript, importing nothing, so the tests
 * can import THESE functions rather than re-implementing them. An earlier
 * version sat inside AppErrorBoundary.tsx, and because that file is TSX the
 * test pasted its own copy of the logic and asserted against the copy. Gutting
 * the real function left the suite green. Adversarial review proved it by
 * inverting the slice and watching 5 of 5 still pass.
 *
 * The whole value of this code is that a screenshot of the error screen NAMES
 * something. A silent regression here puts us straight back to "Minified React
 * error #310" with nowhere to look.
 */

/**
 * The first few frames of a React component stack, which are the ones that
 * name the crashing component. The tail is router and provider chrome that
 * looks identical on every crash and only pushes the useful part off a phone
 * screen.
 */
export function firstFramesOfComponentStack(
  componentStack: unknown,
  limit = 8
): string {
  if (typeof componentStack !== 'string') return '';
  if (!Number.isFinite(limit) || limit <= 0) return '';
  return componentStack
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, limit)
    .join('\n');
}

/** Longest label we put in the crash card, so one frame cannot flood it. */
export const CRASHING_LABEL_MAX = 120;

/**
 * The bundle file a frame points at, reduced to its basename.
 *
 * IN PRODUCTION THIS IS THE ONLY HUMAN READABLE PART OF THE FRAME, and missing
 * it was a real defect caught in review. React does not ship component names to
 * production: `ClassPage` minifies to `Sa`, so a real frame reads
 *
 *     at Sa (https://host/assets/ClassPage-C1owfnmg.js:1:284531)
 *
 * and a label of "Sa" tells Bobbie nothing. The basename is the only readable
 * token left, so it is kept.
 *
 * IT DOES NOT ALWAYS NAME A PAGE, AND AN EARLIER VERSION OF THIS COMMENT SAID IT
 * DID. That was false and adversarial review caught it. Counted on a real build
 * of this branch: 15 of the emitted chunks are not named after any page, because
 * vite.config.ts:27 deliberately groups vendor code into react-, firebase-,
 * sentry- and motion- chunks, and a shared index- chunk holds around thirty of
 * the app's own components including Landing, NavDrawer and this error boundary
 * itself. A crash in any of those reads "Ka (index-DxnGRT92.js)", which locates
 * the bundle and not the page.
 *
 * That is still worth far more than "Ka" alone, and it is exactly why the guide
 * entry promises a code and a file name rather than a page. Turning either into
 * a real component name needs a sourcemap lookup, which is a separate job.
 *
 * Line and column are dropped: they index minified output and mean nothing
 * without that sourcemap.
 */
function bundleFileFromFrame(frame: string): string {
  const matches = frame.match(/[\w.-]+\.(?:[cm]?jsx?|tsx?)(?=[:?#)\s]|$)/g);
  return matches && matches.length > 0 ? matches[matches.length - 1] : '';
}

/**
 * The component name in a frame, across every shape React and the host engine
 * produce.
 *
 * THE FRAME PREFIX IS DECIDED AT RUNTIME BY THE ENGINE, not by React, so there
 * is no single format to bet on. React 18's production build derives it from
 * `Error().stack`: V8 yields "    at Name (url)", while engines whose stacks
 * read `name@url` yield no prefix at all. Both are handled, as is the older
 * "in Name (at File.tsx:41)" development shape.
 *
 * Wrappers are kept whole, so `ForwardRef(Icon)` stays `ForwardRef(Icon)`
 * rather than collapsing to the useless `ForwardRef`.
 */
function componentNameFromFrame(frame: string): string {
  const prefixed = /^(?:in|at)\s+([\w$.]+(?:\([\w$.<>]+\))?)/.exec(frame);
  const atStyle = /^([\w$.]+(?:\([\w$.<>]+\))?)@/.exec(frame);
  const raw = prefixed ? prefixed[1] : atStyle ? atStyle[1] : '';
  // "Object.<anonymous>" arrives as "Object." once the angle brackets are cut.
  return raw.replace(/\.+$/, '');
}

/**
 * A short label naming where the crash happened, for the part of the card a
 * screenshot actually shows.
 *
 * Name and bundle file are BOTH included when both exist, because in production
 * only the second is readable and in development only the first is
 * interesting. When neither parses, the raw frame is returned rather than an
 * empty string: an ugly line still locates the crash, and a blank space is the
 * exact failure this whole change exists to end.
 */
export function crashingComponentLabel(componentStack: unknown): string {
  if (typeof componentStack !== 'string') return '';
  const firstFrame = componentStack
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)[0];
  if (!firstFrame) return '';

  const name = componentNameFromFrame(firstFrame);
  const file = bundleFileFromFrame(firstFrame);
  const label = name && file ? `${name} (${file})` : file || name || firstFrame;
  if (label.length <= CRASHING_LABEL_MAX) return label;
  return `${label.slice(0, CRASHING_LABEL_MAX - 3)}...`;
}
